import React from 'react';
import { Button, Chip, ModalIsland, Typography } from '@saviynt/design-system';

import { CopyInputField } from '../../../shared/components';
import { useInvertedLocalize } from '../../../shared/utils/localization';

import styles from './SuccessDialog.module.css';

interface SuccessDialogProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: string;
  audience: string;
  roles: string[];
  customClaims?: Record<string, string>;
}

/**
 * Shown after successful client creation.
 * Uses kind='success' and hacks the primaryButton to display body content:
 * new clientId (with copy), assigned roles, and custom claims summary.
 */
export function SuccessDialog({
  isOpen,
  onClose,
  clientId,
  audience,
  roles,
  customClaims,
}: SuccessDialogProps): JSX.Element {
  const l = useInvertedLocalize();

  const claimEntries = Object.entries(customClaims ?? {});

  return (
    <ModalIsland
      isOpen={isOpen}
      onClose={onClose}
      kind='success'
      alertTitle={l('Client created successfully')}
      alertSubtitle={l(
        'Your new client has been successfully registered. You can now use the credentials below to authenticate and manage your integrations.'
      )}
      /* Workaround below! Unfortunately, ModalIsland doesn't accept custom content when using a non-default `kind`. */
      primaryButton={
        <div className={styles.SuccessDialog_content}>
          {/* Client ID with copy-to-clipboard */}
          <CopyInputField
            id='success-clientId'
            label={l('Client ID')}
            value={clientId}
            isReadOnly
          />

          {/* Audience (Keycloak realm URL) */}
          {audience && (
            <CopyInputField
              id='success-audience'
              label={l('Audience')}
              value={audience}
              isReadOnly
            />
          )}

          {/* Roles */}
          {roles.length > 0 && (
            <div>
              <Typography kind='label'>{l('Roles')}</Typography>
              <div className={styles.SuccessDialog_chips}>
                {roles.map((role) => (
                  <Chip
                    key={role}
                    label={role}
                    kind='subtleFilled'
                    size='small'
                  />
                ))}
              </div>
            </div>
          )}

          {/* Custom Claims */}
          {claimEntries.length > 0 && (
            <div>
              <Typography kind='label'>{l('Custom Claims')}</Typography>
              <div className={styles.SuccessDialog_claims}>
                {claimEntries.map(([key, value]) => (
                  <Typography key={key} kind='body2'>
                    {l('{claimKey}: {claimValue}', {
                      claimKey: key,
                      claimValue: value,
                    })}
                  </Typography>
                ))}
              </div>
            </div>
          )}
        </div>
      }
      secondaryButton={
        <Button type='button' kind='filled' size='medium' onClick={onClose}>
          {l('Done')}
        </Button>
      }
    />
  );
}
